'use client';
import { useEffect, useState } from 'react';
import type { ReviewList as ReviewListData, ReviewSort, ReviewStats } from '../../lib/reviews-shared';
import { ReviewList } from './ReviewList';

/**
 * **한 강좌의 수강후기** — 강좌 코드만 받아 그 강좌 후기를 그린다(2026-09-07 사용자 지시).
 *
 * 화면은 후기 목록(`/boards/review`)과 **같은 컴포넌트**(`ReviewList`)다 — 별점 요약·베스트 띠·
 * 앨범/리스트 뷰·정렬·쪽 넘김이 그대로다. 전에는 강좌 상세가 자기 마크업을 따로 갖고 있어
 * 한쪽만 고치면 둘이 갈렸다(실제로 베스트가 빠지고 작성자가 비어 있었다).
 *
 * 다른 것은 **데이터를 어디서 받나**와 **정렬·쪽을 어디에 담나** 둘뿐이다 —
 *  · 목록 화면은 서버가 그려 주고(검색 노출), 정렬·쪽은 **주소**에 담는다.
 *  · 여기는 **탭을 열 때** 받고(안 보는 사람에게 비용을 지우지 않는다), 정렬·쪽은 **상태**다.
 *    주소를 바꾸면 상세가 통째로 다시 그려지고 탭이 처음으로 돌아간다.
 *
 * 검색줄은 감춘다 — 한 강좌 안에서 찾을 것이 몇 줄 되지 않는다.
 */
const SIZE = 10;

export function ProductReviews({ productId, stats, base, defaultView, canWrite, backTo }: {
  productId: number;
  /** 별점 요약 — 상세가 SSR 로 이미 받아 둔 값(다시 묻지 않는다) */
  stats: ReviewStats;
  /** 후기 게시판 주소 — 줄을 누르면 그 후기 보기로 간다 */
  base: string;
  defaultView: 'card' | 'list';
  canWrite: boolean;
  /** 후기를 열었다가 [목록으로] 를 누르면 돌아올 자리 — 이 강좌 상세의 후기 탭이다 */
  backTo?: string;
}) {
  const [data, setData] = useState<ReviewListData | null>(null);
  const [sort, setSort] = useState<ReviewSort>('new');
  const [page, setPage] = useState(1);

  /*
   * 목록은 **이펙트에서** 받는다. 렌더 중에 부르면 그릴 때마다 요청이 나가고, 응답이 상태를
   * 바꾸는 순간 또 렌더돼 끝없이 돈다(2026-08-22 같은 실수를 이미 한 번 했다).
   */
  useEffect(() => {
    let alive = true;
    const q = new URLSearchParams({ productId: String(productId), size: String(SIZE), page: String(page) });
    if (sort !== 'new') q.set('sort', sort);
    fetch(`/api/reviews?${q}`)
      .then((r) => (r.ok ? (r.json() as Promise<ReviewListData>) : null))
      .then((d) => { if (alive) setData(d ?? { rows: [], best: [], total: 0, page, size: SIZE }); })
      .catch(() => { if (alive) setData({ rows: [], best: [], total: 0, page, size: SIZE }); });
    return () => { alive = false; };
  }, [productId, sort, page]);

  return (
    <ReviewList
      rows={data?.rows ?? []} best={data?.best ?? []}
      total={data?.total ?? 0} page={page} size={SIZE}
      stats={stats} sort={sort} base={base}
      canWrite={canWrite} defaultView={defaultView}
      onSort={(s) => { setSort(s); setPage(1); }}
      onPage={setPage}
      hideSearch
      loading={data === null}
      backTo={backTo}
    />
  );
}
